import { ChevronLeft, ChevronRight, RefreshCw, Search, Download } from "lucide-react";
import { Dropdown } from "../../components/ui/dropdown";
import { cn } from "../../lib/utils";
import { FilterPopover, type FilterState } from "../../components/ui/filter-popover";
import { DateRangePicker, type DateRangeValue } from "../../components/ui/date-range-picker";

interface CallsToolbarProps {
  dateRange: DateRangeValue;
  onDateRangeChange: (v: DateRangeValue) => void;
  filters: FilterState;
  onFiltersChange: (v: FilterState) => void;
  retryMode: boolean;
  onRetryMode: (v: boolean) => void;
  onScrollLeft: () => void;
  onScrollRight: () => void;
  onRefresh: () => void;
  onExport: () => void;
  rightSlot?: React.ReactNode;
}

export function CallsToolbar(p: CallsToolbarProps) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524] p-3 sm:p-3.5">
      {/* Retry mode toggle */}
      <label className="flex cursor-pointer items-center gap-2">
        <span className="text-sm font-medium text-ink-2">Retry Mode</span>
        <button
          role="switch"
          aria-checked={p.retryMode}
          onClick={() => p.onRetryMode(!p.retryMode)}
          className={cn(
            "relative h-5 w-9 rounded-full transition-colors",
            p.retryMode ? "bg-brand" : "bg-line",
          )}
        >
          <span
            className={cn(
              "absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all",
              p.retryMode ? "left-[18px]" : "left-0.5",
            )}
          />
        </button>
      </label>

      <div className="h-6 w-px bg-line" />

      {/* Global Filter Button */}
      <FilterPopover value={p.filters} onChange={p.onFiltersChange} />

      {/* Date Range Picker */}
      <DateRangePicker value={p.dateRange} onChange={p.onDateRangeChange} />

      {/* Table scroll controls */}
      <div className="flex gap-1">
        <button onClick={p.onScrollLeft} aria-label="Scroll table left"
          className="rounded-lg bg-soft p-2 text-ink-2 transition-colors hover:bg-line">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button onClick={p.onScrollRight} aria-label="Scroll table right"
          className="rounded-lg bg-soft p-2 text-ink-2 transition-colors hover:bg-line">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1" />

      {p.rightSlot}

      <button onClick={p.onRefresh} aria-label="Refresh calls"
        className="rounded-lg p-2 text-ink-2 transition-colors hover:bg-soft hover:text-ink">
        <RefreshCw className="h-4 w-4" />
      </button>

      <button onClick={p.onExport} aria-label="Export calls"
        className="flex items-center gap-2 rounded-lg bg-soft px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-line">
        <Download className="h-4 w-4" />
        Export
      </button>
    </div>
  );
}
